// ─────────────────────────────────────────────────────────────────────────────
// Application typeface
//
// The chosen font is published as a CSS custom property on <html> together
// with a data-app-font marker; css/appFont.css does the actual work of pushing
// it through the app. Keeping the selection in one place means the boot path
// (index.js) and the settings screen cannot drift apart.
//
// "default" deliberately applies nothing at all, so an account that has never
// picked a font keeps exactly the typography the app ships with.
// ─────────────────────────────────────────────────────────────────────────────

export const FONT_STORAGE_KEY = "appFontFamily";
export const DEFAULT_FONT_ID = "default";
export const FONT_CHANGE_EVENT = "appFontChanged";

const SYSTEM_STACK =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

// `stack` is what gets applied app-wide (null = leave the app's own styles
// alone). `preview` is what the settings card renders itself in, so even the
// "keep as is" option shows the face it stands for.
export const FONT_OPTIONS = [
  {
    id: "default",
    name: "Synthium Default",
    category: "Default",
    note: "The typography the app ships with",
    stack: null,
    preview: '"Poppins", "Roboto", sans-serif',
  },
  {
    id: "inter",
    name: "Inter",
    category: "Sans",
    note: "Crisp and neutral, drawn for screens",
    stack: `"Inter", ${SYSTEM_STACK}`,
    preview: '"Inter", sans-serif',
  },
  {
    id: "poppins",
    name: "Poppins",
    category: "Sans",
    note: "Geometric, round and friendly",
    stack: `"Poppins", ${SYSTEM_STACK}`,
    preview: '"Poppins", sans-serif',
  },
  {
    id: "roboto",
    name: "Roboto",
    category: "Sans",
    note: "Familiar and highly legible",
    stack: `"Roboto", ${SYSTEM_STACK}`,
    preview: '"Roboto", sans-serif',
  },
  {
    id: "montserrat",
    name: "Montserrat",
    category: "Sans",
    note: "Wide and confident, strong headings",
    stack: `"Montserrat", ${SYSTEM_STACK}`,
    preview: '"Montserrat", sans-serif',
  },
  {
    id: "lato",
    name: "Lato",
    category: "Sans",
    note: "Warm humanist shapes, easy on the eye",
    stack: `"Lato", ${SYSTEM_STACK}`,
    preview: '"Lato", sans-serif',
  },
  {
    id: "nunito",
    name: "Nunito",
    category: "Sans",
    note: "Soft rounded terminals, approachable",
    stack: `"Nunito", ${SYSTEM_STACK}`,
    preview: '"Nunito", sans-serif',
  },
  {
    id: "merriweather",
    name: "Merriweather",
    category: "Serif",
    note: "Classic serif, built for long reading",
    stack: '"Merriweather", Georgia, "Times New Roman", serif',
    preview: '"Merriweather", Georgia, serif',
  },
  {
    id: "system",
    name: "System",
    category: "System",
    note: "Whatever your operating system uses",
    stack: SYSTEM_STACK,
    preview: SYSTEM_STACK,
  },
];

export const getFontOption = (id) =>
  FONT_OPTIONS.find((f) => f.id === id) || FONT_OPTIONS[0];

export const getSavedFontId = () => {
  try {
    const saved = localStorage.getItem(FONT_STORAGE_KEY);
    return saved && FONT_OPTIONS.some((f) => f.id === saved) ? saved : DEFAULT_FONT_ID;
  } catch {
    return DEFAULT_FONT_ID;
  }
};

/**
 * Pushes a font to the document. Safe to call before React mounts, which is
 * how the boot path avoids a flash of the previous typeface.
 */
export const applyAppFont = (id = getSavedFontId()) => {
  const option = getFontOption(id);
  const root = document.documentElement;

  if (option.stack) {
    root.style.setProperty("--app-font-family", option.stack);
  } else {
    root.style.removeProperty("--app-font-family");
  }
  root.setAttribute("data-app-font", option.id);

  return option;
};

/** Saves the choice and applies it immediately, in every open component. */
export const setAppFont = (id) => {
  try {
    localStorage.setItem(FONT_STORAGE_KEY, id);
  } catch {
    // Preference is not persisted, but the current session still updates.
  }
  const option = applyAppFont(id);
  window.dispatchEvent(new CustomEvent(FONT_CHANGE_EVENT, { detail: { id: option.id } }));
  return option;
};
